import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  CONDITION_TEMPLATE_FIELDS,
  CONDITION_TYPES,
  EXAM_MANEUVERS,
  GLOBAL_PERIOD_RELATIONSHIPS,
  LACHMAN_GRADES,
  MANEUVER_RESULTS,
  ORTHO_GLOBAL_PERIOD_CPT_CODES,
  PRO_INSTRUMENTS,
  conditionTypeLabel,
  decide,
  examManeuverLabel,
  findOrthoGlobalPeriodCptCode,
  globalPeriodRelationshipLabel,
  lachmanGradeLabel,
  maneuverResultLabel,
  maneuverSupportsGrade,
  proInstrumentById,
  templateFieldFor,
  type BodyMapRegion,
  type BodyMapSide,
  type ConditionEpisodeResponse,
  type ConditionType,
  type ExamManeuverResponse,
  type ExamManeuver,
  type GlobalSurgicalPeriodResponse,
  type GlobalPeriodRelationship,
  type ImplantUdiRecordResponse,
  type LachmanGrade,
  type ManeuverResult,
  type PatientReportedOutcomeResponse,
} from '@emr/shared';

import {
  createConditionEpisode,
  createExamManeuver,
  createGlobalSurgicalPeriod,
  createImplantUdiRecord,
  createPatientReportedOutcome,
  fetchConditionEpisodes,
  fetchExamManeuvers,
  fetchGlobalPeriodModifier,
  fetchGlobalSurgicalPeriods,
  fetchImplantUdiRecords,
  fetchPatientReportedOutcomes,
} from '../api/orthopedics.ts';
import { useGrants } from '../auth/sessionContext.ts';
import { BodyMapPicker } from '../components/BodyMapPicker.tsx';
import { Button, ErrorText, Field, Panel, TextAreaField } from '../components/primitives.tsx';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { QuerySection } from '../components/QuerySection.tsx';

/**
 * The orthopedics module's real, patient-scoped surface (`BACKLOG.md` M7). Reachable directly by URL
 * even with the catalog entry showing an upgrade prompt for an unlicensed org, matching
 * `PatientDermatologyPage.tsx`/`PatientAestheticsPage.tsx`'s own stated reasoning for re-applying
 * `GatedRoute` here.
 *
 * Five sections, each independently queried and independently writable behind `chart:write`:
 *
 *   - **Condition episodes** — fracture/joint replacement/ACL/rotator cuff/carpal tunnel, each with
 *     its own small structured field set (`@emr/shared`'s `conditionTemplates.ts`).
 *   - **Exam maneuvers** — Lachman/Neer/Hawkins-Kennedy/Tinel's/Phalen's, graded, sited via the shared
 *     `BodyMapPicker` (extended with joint regions for this module — `bodyMapping.ts`'s own header).
 *   - **Patient-reported outcomes** — instrument + date + score only, never item-level content, for
 *     any instrument (PROMIS included) — see `@emr/shared`'s `patientReportedOutcomes.ts` header.
 *   - **Global surgical periods** — `globalPeriodDays` is always server-derived from the CPT code, and
 *     the modifier calculator recomputes a real modifier from a real stored procedure date every call.
 *   - **Implant UDI records** — a raw captured barcode, always kept, with structured fields parsed
 *     from it where the format is understood.
 */
export function PatientOrthopedicsPage() {
  const { t } = useTranslation('orthopedics');
  const { patientId } = useParams<{ patientId: string }>();

  if (patientId === undefined) {
    return <p className="m-0 text-sm text-danger">{t('common.noPatientId')}</p>;
  }

  return (
    <GatedRoute title={t('pageTitle')} capability="chart:read" feature="orthopedicsModule">
      <div className="flex flex-col gap-6">
        <ConditionEpisodeSection patientId={patientId} />
        <ExamManeuverSection patientId={patientId} />
        <ProSection patientId={patientId} />
        <GlobalSurgicalPeriodSection patientId={patientId} />
        <ImplantUdiSection patientId={patientId} />
      </div>
    </GatedRoute>
  );
}

function SectionHeading({ children }: { readonly children: string }) {
  return <h2 className="m-0 text-lg font-semibold text-ink">{children}</h2>;
}

const WRAP_LONG_TEXT = 'break-words whitespace-normal [overflow-wrap:anywhere]';

// --- Condition episodes ------------------------------------------------------------------------------

function ConditionEpisodeSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('orthopedics');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['orthopedics', 'condition-episodes', patientId];

  const query = useQuery<readonly ConditionEpisodeResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchConditionEpisodes(patientId, signal),
  });

  const [conditionType, setConditionType] = useState<ConditionType>('fracture');
  const [bodyMapRegion, setBodyMapRegion] = useState<BodyMapRegion>('wrist');
  const [bodyMapSide, setBodyMapSide] = useState<BodyMapSide | null>(null);
  const [diagnosedOn, setDiagnosedOn] = useState('');
  const [templateFields, setTemplateFields] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: () =>
      createConditionEpisode(patientId, {
        conditionType,
        bodyMapRegion,
        bodyMapSide,
        diagnosedOn,
        templateFields,
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setDiagnosedOn('');
      setTemplateFields({});
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('conditionEpisodes.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('conditionEpisodes.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('conditionEpisodes.emptyMessage')}
        >
          {(episodes) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {episodes.map((episode) => {
                const fields = Object.entries(episode.templateFields);
                return (
                  <li
                    key={episode.id}
                    className="flex flex-col gap-1 rounded-control border border-line p-3 text-sm"
                  >
                    <p className="m-0 font-medium text-ink">
                      {isConditionType(episode.conditionType)
                        ? conditionTypeLabel(episode.conditionType)
                        : episode.conditionType}
                    </p>
                    <p className={`m-0 text-ink-muted ${WRAP_LONG_TEXT}`}>
                      {t(`vocab.bodyMapRegions.${episode.bodyMapRegion}`)}
                      {episode.bodyMapSide !== null
                        ? ` (${t(`vocab.bodyMapSides.${episode.bodyMapSide}`)})`
                        : ''}
                      {' — '}
                      {t('conditionEpisodes.diagnosedOnSuffix', { date: episode.diagnosedOn })}
                    </p>
                    {fields.length > 0 ? (
                      <ul className="m-0 flex list-none flex-col gap-0.5 p-0 pl-2 text-ink-muted">
                        {fields.map(([key, value]) => (
                          <li key={key} className={WRAP_LONG_TEXT}>
                            {isConditionType(episode.conditionType)
                              ? (templateFieldFor(episode.conditionType, key)?.label ?? key)
                              : key}
                            : {value}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">
                  {t('conditionEpisodes.conditionTypeLabel')}
                </span>
                <select
                  value={conditionType}
                  onChange={(event) => {
                    const next = event.target.value as ConditionType;
                    setConditionType(next);
                    setTemplateFields({});
                  }}
                  className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                >
                  {CONDITION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {conditionTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </label>
              <Field
                label={t('conditionEpisodes.diagnosedOnLabel')}
                type="date"
                value={diagnosedOn}
                onChange={(event) => {
                  setDiagnosedOn(event.target.value);
                }}
                required
              />
            </div>

            <BodyMapPicker
              region={bodyMapRegion}
              side={bodyMapSide}
              onChange={(next) => {
                setBodyMapRegion(next.region);
                setBodyMapSide(next.side);
              }}
              groupLabel={t('conditionEpisodes.siteRegionLabel')}
              regionLabel={(region) => t(`vocab.bodyMapRegions.${region}`)}
              sideLabel={(side) => t(`vocab.bodyMapSides.${side}`)}
              notApplicableLabel={t('common.notApplicable')}
            />

            {CONDITION_TEMPLATE_FIELDS[conditionType].length > 0 ? (
              <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                <legend className="p-0 text-sm font-medium text-ink">
                  {t('conditionEpisodes.detailsHeading')}
                </legend>
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                  {CONDITION_TEMPLATE_FIELDS[conditionType].map((field) =>
                    field.values !== undefined ? (
                      <label key={field.key} className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-ink">{field.label}</span>
                        <select
                          value={templateFields[field.key] ?? ''}
                          onChange={(event) => {
                            setTemplateFields((prev) => ({
                              ...prev,
                              [field.key]: event.target.value,
                            }));
                          }}
                          className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                        >
                          <option value="" disabled>
                            {t('common.select')}
                          </option>
                          {field.values.map((value) => (
                            <option key={value} value={value}>
                              {value}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <Field
                        key={field.key}
                        label={field.label}
                        value={templateFields[field.key] ?? ''}
                        onChange={(event) => {
                          setTemplateFields((prev) => ({
                            ...prev,
                            [field.key]: event.target.value,
                          }));
                        }}
                      />
                    ),
                  )}
                </div>
              </fieldset>
            ) : null}

            <div>
              <Button type="submit" pending={create.isPending}>
                {t('conditionEpisodes.recordButton')}
              </Button>
              {create.isError ? <ErrorText message={t('conditionEpisodes.createError')} /> : null}
            </div>
          </form>
        ) : null}
      </Panel>
    </section>
  );
}

function isConditionType(value: string): value is ConditionType {
  return (CONDITION_TYPES as readonly string[]).includes(value);
}

// --- Exam maneuvers -------------------------------------------------------------------------------

function isExamManeuver(value: string): value is ExamManeuver {
  return (EXAM_MANEUVERS as readonly string[]).includes(value);
}

function isManeuverResult(value: string): value is ManeuverResult {
  return (MANEUVER_RESULTS as readonly string[]).includes(value);
}

function isLachmanGrade(value: string): value is LachmanGrade {
  return (LACHMAN_GRADES as readonly string[]).includes(value);
}

function ExamManeuverSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('orthopedics');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['orthopedics', 'exam-maneuvers', patientId];

  const query = useQuery<readonly ExamManeuverResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchExamManeuvers(patientId, signal),
  });

  const [maneuver, setManeuver] = useState<ExamManeuver>('lachman');
  const [result, setResult] = useState<ManeuverResult>('positive');
  const [grade, setGrade] = useState<LachmanGrade | null>(null);
  const [bodyMapRegion, setBodyMapRegion] = useState<BodyMapRegion>('knee');
  const [bodyMapSide, setBodyMapSide] = useState<BodyMapSide | null>('right');
  const [performedOn, setPerformedOn] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createExamManeuver(patientId, {
        maneuver,
        result,
        grade,
        bodyMapRegion,
        bodyMapSide,
        performedOn,
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setPerformedOn('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('examManeuvers.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('examManeuvers.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('examManeuvers.emptyMessage')}
        >
          {(maneuvers) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {maneuvers.map((entry) => (
                <li
                  key={entry.id}
                  className="flex flex-col gap-1 rounded-control border border-line p-3 text-sm"
                >
                  <p className="m-0 font-medium text-ink">
                    {isExamManeuver(entry.maneuver)
                      ? examManeuverLabel(entry.maneuver)
                      : entry.maneuver}
                    {' — '}
                    {isManeuverResult(entry.result)
                      ? maneuverResultLabel(entry.result)
                      : entry.result}
                    {entry.grade !== null && isLachmanGrade(entry.grade)
                      ? ` (${lachmanGradeLabel(entry.grade)})`
                      : ''}
                  </p>
                  <p className="m-0 text-ink-muted">
                    {t(`vocab.bodyMapRegions.${entry.bodyMapRegion}`)}
                    {entry.bodyMapSide !== null
                      ? ` (${t(`vocab.bodyMapSides.${entry.bodyMapSide}`)})`
                      : ''}
                    {' — '}
                    {t('examManeuvers.performedOnSuffix', { date: entry.performedOn })}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">
                  {t('examManeuvers.maneuverLabel')}
                </span>
                <select
                  value={maneuver}
                  onChange={(event) => {
                    const next = event.target.value as ExamManeuver;
                    setManeuver(next);
                    if (!maneuverSupportsGrade(next)) setGrade(null);
                  }}
                  className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                >
                  {EXAM_MANEUVERS.map((value) => (
                    <option key={value} value={value}>
                      {examManeuverLabel(value)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">
                  {t('examManeuvers.resultLabel')}
                </span>
                <select
                  value={result}
                  onChange={(event) => {
                    setResult(event.target.value as ManeuverResult);
                  }}
                  className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                >
                  {MANEUVER_RESULTS.map((value) => (
                    <option key={value} value={value}>
                      {maneuverResultLabel(value)}
                    </option>
                  ))}
                </select>
              </label>
              {maneuverSupportsGrade(maneuver) ? (
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-ink">
                    {t('examManeuvers.gradeLabel')}
                  </span>
                  <select
                    value={grade ?? ''}
                    onChange={(event) => {
                      const raw = event.target.value;
                      setGrade(raw === '' ? null : (raw as LachmanGrade));
                    }}
                    className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                  >
                    <option value="">{t('common.notApplicable')}</option>
                    {LACHMAN_GRADES.map((value) => (
                      <option key={value} value={value}>
                        {lachmanGradeLabel(value)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <Field
                label={t('examManeuvers.performedOnLabel')}
                type="date"
                value={performedOn}
                onChange={(event) => {
                  setPerformedOn(event.target.value);
                }}
                required
              />
            </div>

            <BodyMapPicker
              region={bodyMapRegion}
              side={bodyMapSide}
              onChange={(next) => {
                setBodyMapRegion(next.region);
                setBodyMapSide(next.side);
              }}
              groupLabel={t('examManeuvers.siteRegionLabel')}
              regionLabel={(region) => t(`vocab.bodyMapRegions.${region}`)}
              sideLabel={(side) => t(`vocab.bodyMapSides.${side}`)}
              notApplicableLabel={t('common.notApplicable')}
            />

            <div>
              <Button type="submit" pending={create.isPending}>
                {t('examManeuvers.recordButton')}
              </Button>
              {create.isError ? <ErrorText message={t('examManeuvers.createError')} /> : null}
            </div>
          </form>
        ) : null}
      </Panel>
    </section>
  );
}

// --- Patient-reported outcomes -----------------------------------------------------------------------

function ProSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('orthopedics');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['orthopedics', 'patient-reported-outcomes', patientId];

  const query = useQuery<readonly PatientReportedOutcomeResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchPatientReportedOutcomes(patientId, signal),
  });

  const [instrumentId, setInstrumentId] = useState<string>(
    PRO_INSTRUMENTS[0]?.id ?? 'promis_global10',
  );
  const [administeredOn, setAdministeredOn] = useState('');
  const [rawScore, setRawScore] = useState('');
  const [notes, setNotes] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createPatientReportedOutcome(patientId, {
        instrumentId,
        administeredOn,
        rawScore,
        notes: notes === '' ? undefined : notes,
        confidentialityReasons: [],
      }),
    onSuccess: () => {
      setAdministeredOn('');
      setRawScore('');
      setNotes('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('pro.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('pro.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('pro.emptyMessage')}
        >
          {(outcomes) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {outcomes.map((outcome) => {
                const instrument = proInstrumentById(outcome.instrumentId);
                return (
                  <li
                    key={outcome.id}
                    className="flex flex-col gap-1 rounded-control border border-line p-3 text-sm"
                  >
                    <p className={`m-0 font-medium text-ink ${WRAP_LONG_TEXT}`}>
                      {instrument?.label ?? outcome.instrumentId}{' '}
                      <span className="text-xs font-normal text-ink-muted">
                        (
                        {instrument?.licenseStatus === 'public_domain'
                          ? t('pro.publicDomainBadge')
                          : t('pro.licensedBadge')}
                        )
                      </span>
                    </p>
                    <p className={`m-0 text-ink-muted ${WRAP_LONG_TEXT}`}>
                      {t('pro.administeredOnSuffix', { date: outcome.administeredOn })}
                      {' — '}
                      {t('pro.rawScoreLabel')}: {outcome.rawScore}
                    </p>
                    {outcome.notes !== null ? (
                      <p className={`m-0 text-ink-muted ${WRAP_LONG_TEXT}`}>{outcome.notes}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">{t('pro.instrumentLabel')}</span>
                <select
                  value={instrumentId}
                  onChange={(event) => {
                    setInstrumentId(event.target.value);
                  }}
                  className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                >
                  {PRO_INSTRUMENTS.map((instrument) => (
                    <option key={instrument.id} value={instrument.id}>
                      {instrument.label}
                      {' — '}
                      {instrument.licenseStatus === 'public_domain'
                        ? t('pro.publicDomainBadge')
                        : t('pro.licensedBadge')}
                    </option>
                  ))}
                </select>
              </label>
              <Field
                label={t('pro.administeredOnLabel')}
                type="date"
                value={administeredOn}
                onChange={(event) => {
                  setAdministeredOn(event.target.value);
                }}
                required
              />
              <Field
                label={t('pro.rawScoreLabel')}
                value={rawScore}
                onChange={(event) => {
                  setRawScore(event.target.value);
                }}
                required
              />
            </div>
            <TextAreaField
              label={t('pro.notesLabel')}
              value={notes}
              onChange={(event) => {
                setNotes(event.target.value);
              }}
            />
            <div>
              <Button type="submit" pending={create.isPending}>
                {t('pro.recordButton')}
              </Button>
              {create.isError ? <ErrorText message={t('pro.createError')} /> : null}
            </div>
          </form>
        ) : null}
      </Panel>
    </section>
  );
}

// --- Global surgical periods -------------------------------------------------------------------

function GlobalSurgicalPeriodSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('orthopedics');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['orthopedics', 'global-surgical-periods', patientId];

  const query = useQuery<readonly GlobalSurgicalPeriodResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchGlobalSurgicalPeriods(patientId, signal),
  });

  const [cptCode, setCptCode] = useState<string>(ORTHO_GLOBAL_PERIOD_CPT_CODES[0]?.code ?? '');
  const [procedureDate, setProcedureDate] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createGlobalSurgicalPeriod(patientId, { cptCode, procedureDate, confidentialityReasons: [] }),
    onSuccess: () => {
      setProcedureDate('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const [modifierPeriodId, setModifierPeriodId] = useState('');
  const [serviceDate, setServiceDate] = useState('');
  const [relationship, setRelationship] = useState<GlobalPeriodRelationship>('unrelated_em');

  const checkModifier = useMutation({
    mutationFn: () =>
      fetchGlobalPeriodModifier(patientId, modifierPeriodId, { serviceDate, relationship }),
  });

  const periods = query.data ?? [];

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('globalSurgicalPeriods.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('globalSurgicalPeriods.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('globalSurgicalPeriods.emptyMessage')}
        >
          {() => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {periods.map((period) => {
                const catalogEntry = findOrthoGlobalPeriodCptCode(period.cptCode);
                return (
                  <li
                    key={period.id}
                    className="flex flex-col gap-1 rounded-control border border-line p-3 text-sm"
                  >
                    <p className={`m-0 font-medium text-ink ${WRAP_LONG_TEXT}`}>
                      {period.cptCode}
                      {catalogEntry !== undefined ? ` — ${catalogEntry.description}` : ''}
                    </p>
                    <p className="m-0 text-ink-muted">
                      {period.procedureDate}
                      {' — '}
                      {t('globalSurgicalPeriods.globalPeriodSuffix', {
                        days: period.globalPeriodDays,
                        date: period.windowEndDate,
                      })}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4 sm:flex-row sm:flex-wrap sm:items-end"
          >
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">
                {t('globalSurgicalPeriods.cptCodeLabel')}
              </span>
              <select
                value={cptCode}
                onChange={(event) => {
                  setCptCode(event.target.value);
                }}
                className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
              >
                {ORTHO_GLOBAL_PERIOD_CPT_CODES.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {entry.code} — {entry.description}
                  </option>
                ))}
              </select>
            </label>
            <Field
              label={t('globalSurgicalPeriods.procedureDateLabel')}
              type="date"
              value={procedureDate}
              onChange={(event) => {
                setProcedureDate(event.target.value);
              }}
              required
            />
            <Button type="submit" pending={create.isPending}>
              {t('globalSurgicalPeriods.recordButton')}
            </Button>
            {create.isError ? <ErrorText message={t('globalSurgicalPeriods.createError')} /> : null}
          </form>
        ) : null}

        {periods.length > 0 ? (
          <div className="mt-6 flex flex-col gap-3 border-t border-row-line pt-4">
            <h3 className="m-0 text-sm font-semibold text-ink">
              {t('globalSurgicalPeriods.modifierHeading')}
            </h3>
            <p className="m-0 text-sm text-ink-muted">
              {t('globalSurgicalPeriods.modifierDescription')}
            </p>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                checkModifier.mutate();
              }}
              className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
            >
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">
                  {t('globalSurgicalPeriods.modifierProcedureLabel')}
                </span>
                <select
                  value={modifierPeriodId}
                  onChange={(event) => {
                    setModifierPeriodId(event.target.value);
                  }}
                  className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                >
                  <option value="" disabled>
                    {t('common.select')}
                  </option>
                  {periods.map((period) => (
                    <option key={period.id} value={period.id}>
                      {period.cptCode} ({period.procedureDate})
                    </option>
                  ))}
                </select>
              </label>
              <Field
                label={t('globalSurgicalPeriods.modifierServiceDateLabel')}
                type="date"
                value={serviceDate}
                onChange={(event) => {
                  setServiceDate(event.target.value);
                }}
                required
              />
              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-ink">
                  {t('globalSurgicalPeriods.modifierRelationshipLabel')}
                </span>
                <select
                  value={relationship}
                  onChange={(event) => {
                    setRelationship(event.target.value as GlobalPeriodRelationship);
                  }}
                  className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
                >
                  {GLOBAL_PERIOD_RELATIONSHIPS.map((value) => (
                    <option key={value} value={value}>
                      {globalPeriodRelationshipLabel(value)}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                type="submit"
                pending={checkModifier.isPending}
                disabled={modifierPeriodId === ''}
              >
                {t('globalSurgicalPeriods.modifierCheckButton')}
              </Button>
            </form>
            {checkModifier.isError ? (
              <ErrorText message={t('globalSurgicalPeriods.modifierError')} />
            ) : null}
            {checkModifier.isSuccess ? (
              <p className="m-0 text-sm font-medium text-ink">
                {checkModifier.data.modifier !== null
                  ? t('globalSurgicalPeriods.modifierResultWithModifier', {
                      modifier: checkModifier.data.modifier,
                      reason: t(
                        `globalSurgicalPeriods.modifierReason.${checkModifier.data.reason}`,
                      ),
                    })
                  : t('globalSurgicalPeriods.modifierResultNone', {
                      reason: t(
                        `globalSurgicalPeriods.modifierReason.${checkModifier.data.reason}`,
                      ),
                    })}
              </p>
            ) : null}
          </div>
        ) : null}
      </Panel>
    </section>
  );
}

// --- Implant UDI records ---------------------------------------------------------------------------

function ImplantUdiSection({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('orthopedics');
  const grants = useGrants();
  const mayWrite = decide(grants, 'chart:write');
  const queryClient = useQueryClient();
  const queryKey = ['orthopedics', 'implant-udi-records', patientId];

  const query = useQuery<readonly ImplantUdiRecordResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchImplantUdiRecords(patientId, signal),
  });

  const [rawBarcode, setRawBarcode] = useState('');
  const [implantedOn, setImplantedOn] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createImplantUdiRecord(patientId, { rawBarcode, implantedOn, confidentialityReasons: [] }),
    onSuccess: () => {
      setRawBarcode('');
      setImplantedOn('');
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  return (
    <section className="flex flex-col gap-2">
      <SectionHeading>{t('implantUdi.heading')}</SectionHeading>
      <p className="m-0 text-sm text-ink-muted">{t('implantUdi.description')}</p>
      <Panel>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('implantUdi.emptyMessage')}
        >
          {(records) => (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {records.map((record) => (
                <li
                  key={record.id}
                  className="flex flex-col gap-1 rounded-control border border-line p-3 text-sm"
                >
                  <p className={`m-0 font-medium text-ink ${WRAP_LONG_TEXT}`}>
                    {record.rawBarcode}
                  </p>
                  <p className="m-0 text-ink-muted">
                    {t('implantUdi.formatLabel')}: {record.barcodeFormat}
                    {' — '}
                    {t('implantUdi.implantedOnLabel')} {record.implantedOn}
                  </p>
                  {record.deviceIdentifier !== null ? (
                    <p className={`m-0 text-ink-muted ${WRAP_LONG_TEXT}`}>
                      {t('implantUdi.deviceIdentifierLabel')}: {record.deviceIdentifier}
                      {record.lotNumber !== null
                        ? ` · ${t('implantUdi.lotNumberLabel')}: ${record.lotNumber}`
                        : ''}
                      {record.serialNumber !== null
                        ? ` · ${t('implantUdi.serialNumberLabel')}: ${record.serialNumber}`
                        : ''}
                      {record.expirationDate !== null
                        ? ` · ${t('implantUdi.expirationDateLabel')}: ${record.expirationDate}`
                        : ''}
                      {record.manufactureDate !== null
                        ? ` · ${t('implantUdi.manufactureDateLabel')}: ${record.manufactureDate}`
                        : ''}
                    </p>
                  ) : (
                    <p className="m-0 text-ink-muted">{t('implantUdi.notParsedNotice')}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </QuerySection>

        {mayWrite ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}
            className="mt-4 flex flex-col gap-3 border-t border-row-line pt-4"
          >
            <Field
              label={t('implantUdi.rawBarcodeLabel')}
              value={rawBarcode}
              onChange={(event) => {
                setRawBarcode(event.target.value);
              }}
              required
            />
            <Field
              label={t('implantUdi.implantedOnLabel')}
              type="date"
              value={implantedOn}
              onChange={(event) => {
                setImplantedOn(event.target.value);
              }}
              required
            />
            <div>
              <Button type="submit" pending={create.isPending}>
                {t('implantUdi.recordButton')}
              </Button>
              {create.isError ? <ErrorText message={t('implantUdi.createError')} /> : null}
            </div>
          </form>
        ) : null}
      </Panel>
    </section>
  );
}
